import { languageConfig } from '../../config/languageConfig';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import DrillShell from '../DrillShell';
import SpecialCharBar from '../SpecialCharBar';
import { buildNoRepeatQueue, shuffle, speak, gradeTypedAnswer, sanitiseForTTS } from '../../utils/helpers';

const BLANK = '_____';

function buildBlank(sentence, targetEs) {
  if (typeof sentence !== 'string' || !sentence.trim()) return null;
  const target = (targetEs || '').trim();
  if (!target) return { kind: 'plain', sentence };
  const lowerSentence = sentence.toLowerCase();
  let i = lowerSentence.indexOf(target.toLowerCase());
  let matchLength = target.length;
  // Regular Spanish adjectives alternate -o/-a for gender agreement — the dictionary
  // form may not match the sentence's agreeing form (e.g. "frío" vs "está fría").
  if (i === -1 && /[oa]$/i.test(target)) {
    const altTarget = target.slice(0, -1) + (target.slice(-1).toLowerCase() === 'o' ? 'a' : 'o');
    i = lowerSentence.indexOf(altTarget.toLowerCase());
    matchLength = altTarget.length;
  }
  if (i === -1) return { kind: 'plain', sentence };
  return {
    kind: 'blank',
    before: sentence.slice(0, i),
    after: sentence.slice(i + matchLength),
  };
}

export default function VocabFillBlankDrill({
  mode = 'typed', words, progress, onAnswer, onDone, onBack,
  drillLength = 10, headerOffset = 0, counterOverride, strictMode = false, skipControl,
}) {
  const isTyped = mode === 'typed';

  const pool = useMemo(
    () => {
      const filtered = Array.isArray(words)
        ? words.filter(w => w && w.es && (
            (typeof w.contextSentence === 'string' && w.contextSentence.trim()) ||
            (Array.isArray(w.contextSentence) && w.contextSentence.length > 0)
          ))
        : [];
      return filtered;
    },
    [words],
  );

  const queueRef = useRef(null);
  if (!queueRef.current) {
    queueRef.current = buildNoRepeatQueue(pool, progress, drillLength);
  }
  const queue = queueRef.current;

  const [idx, setIdx] = useState(0);
  const [val, setVal] = useState('');
  const [picked, setPicked] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [correct, setCorrect] = useState(0);
  const inputRef = useRef(null);

  const word = queue[idx];

  useEffect(() => { if (isTyped) inputRef.current?.focus(); }, [idx, isTyped]);

  const wordEs = word ? word.es : null;
  const choices = useMemo(() => {
    if (isTyped || !word) return [];
    const others = pool.filter(w => w.es !== wordEs);
    const distractors = shuffle(others).slice(0, 3).map(w => w.es);
    return shuffle([wordEs, ...distractors]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordEs, isTyped, pool]);

  if (!queue.length) {
    return (
      <DrillShell title="Vocab Fill in the Blank" current={0} total={0} onBack={onBack} headerOffset={headerOffset}>
        <div className="text-center py-10" style={{ color: 'hsl(var(--muted-foreground))' }}>
          No words available
        </div>
      </DrillShell>
    );
  }

  const sentence = Array.isArray(word.contextSentence)
    ? word.contextSentence[Math.floor(Math.random() * word.contextSentence.length)]
    : (word.contextSentence || '');
  const parts = buildBlank(sentence, word.es);
  const answered = isTyped ? !!feedback : !!picked;
  const wasCorrect = isTyped ? (feedback && feedback.ok) : (picked === word.es);

  const submit = () => {
    if (feedback || !val.trim()) return;
    const { ok, closeEnough, displayTarget } = gradeTypedAnswer(val, word.es, strictMode);
    setFeedback({ ok, closeEnough, target: displayTarget });
    onAnswer(word.es, ok);
    if (ok) setCorrect(c => c + 1);
  };

  const handlePick = (ans) => {
    if (picked) return;
    const ok = ans === word.es;
    setPicked(ans);
    onAnswer(word.es, ok);
    if (ok) setCorrect(c => c + 1);
  };

  const next = () => {
    if (idx + 1 >= queue.length) { onDone(correct, queue.length); return; }
    setIdx(idx + 1); setVal(''); setPicked(null); setFeedback(null);
  };

  const blankBg = !answered ? 'hsl(var(--muted))' : (wasCorrect ? '#DCFCE7' : '#FEE2E2');
  const blankColor = !answered ? 'hsl(var(--muted-foreground))' : (wasCorrect ? '#14532D' : '#991B1B');

  // The blank itself — a typeable input while unanswered in typed mode, otherwise a
  // colored pill. Shared between the inline blank (parts.kind === 'blank') and the
  // plain-sentence fallback (parts.kind === 'plain', when the target word couldn't be
  // located in the sentence) so typed mode always has an interactive input either way.
  const blankSlot = isTyped && !answered ? (
    <input
      ref={inputRef}
      type="text"
      value={val}
      onChange={e => setVal(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') submit(); }}
      disabled={!!feedback}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      data-testid="vfb-input"
      style={{
        display: 'inline-block',
        background: 'hsl(var(--card))',
        color: 'hsl(var(--foreground))',
        minWidth: 80,
        width: Math.max(80, (val.length + 2) * 20) + 'px',
        fontSize: '0.95em',
        fontFamily: 'inherit',
        fontWeight: 700,
        textAlign: 'center',
        border: '2px solid hsl(var(--primary))',
        borderRadius: 8,
        padding: '2px 8px',
        outline: 'none',
        verticalAlign: 'middle',
      }}
    />
  ) : (
    <span className="px-2 rounded-md" style={{ background: blankBg, color: blankColor }}>
      {answered ? word.es : BLANK}
    </span>
  );

  return (
    <DrillShell
      title="Vocab Fill in the Blank"
      subtitle={isTyped ? 'Read the sentence — type the missing word' : 'Read the sentence — pick the missing word'}
      current={idx + 1} total={queue.length} onBack={onBack}
      headerOffset={headerOffset} counterOverride={counterOverride}
      skipControl={skipControl}
      footer={answered ? (
        <button onClick={next} data-testid="vfb-continue"
          className="w-full py-3 rounded-xl font-bold text-white text-sm flex items-center justify-center gap-2"
          style={{ background: 'hsl(var(--primary))' }}>
          {idx + 1 >= queue.length ? 'Finish ✓' : 'Continue →'} <ArrowRight size={16} />
        </button>
      ) : isTyped && (
        <button data-testid="vfb-check" onClick={submit} disabled={!val.trim()}
          className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-50"
          style={{ background: 'hsl(var(--primary))' }}>
          Check
        </button>
      )}>

      <div className="rounded-2xl p-4 mb-2 text-center"
        style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
        <div className="font-serif text-xl font-black leading-relaxed flex items-center justify-center gap-1 flex-wrap"
          style={{ color: 'hsl(var(--foreground))' }} data-testid="vfb-sentence">
          {parts.kind === 'blank' ? (
            <>
              <span>{parts.before}</span>
              {blankSlot}
              <span>{parts.after}</span>
            </>
          ) : (
            <span>{parts.sentence}</span>
          )}
        </div>
        {parts.kind === 'plain' && (
          <div className="mt-2 inline-flex items-center justify-center">
            {blankSlot}
          </div>
        )}
        {isTyped && !feedback && strictMode && (
          <div className="mt-3">
            <SpecialCharBar inputRef={inputRef} value={val} onChange={setVal} />
          </div>
        )}
        <div className="text-xs mt-2" style={{ color: 'hsl(var(--muted-foreground))' }} data-testid="vfb-hint">
          {word.en}
        </div>
      </div>

      {isTyped ? (
        feedback && (
          <div className="text-center py-2.5 rounded-xl mb-1"
            style={{ background: feedback.ok ? '#DCFCE7' : '#FEE2E2', color: feedback.ok ? '#14532D' : '#991B1B' }}>
            <div className="font-bold text-sm" data-testid="vfb-feedback">
              {feedback.ok ? (feedback.closeEnough ? 'Almost! ✓' : 'Correct! ✓') : 'Answer:'}
            </div>
            <div className="text-base font-black mt-1 flex items-center justify-center gap-2">
              {feedback.target}
              <button onClick={() => speak(sanitiseForTTS(word.es), languageConfig.sourceLanguage)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>🔊</button>
            </div>
          </div>
        )
      ) : (
        <div className="grid grid-cols-2 gap-2.5">
          {choices.map((c, i) => {
            const isCorrect = picked && c === word.es;
            const isWrong = picked === c && c !== word.es;
            return (
              <button key={c + i} data-testid={`vfb-choice-${i}`} disabled={!!picked} onClick={() => handlePick(c)}
                className={`choice-btn ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''}`}>
                {c}
              </button>
            );
          })}
        </div>
      )}
    </DrillShell>
  );
}
